import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { keyBy } from 'lodash';
import { getValidator } from '../../utilities/validations/error-rules';
import { codeNoSpecialCharacter } from '../../utilities/validations/validation-rules';

yup.addMethod(
  yup.string,
  'afpValidate',
  function callback(errorMessage, field, isValid) {
    return this.test(field, errorMessage, function getError() {
      const { path, createError } = this;
      return isValid || createError({ path, message: errorMessage });
    });
  },
);

const fuelTypeFields = (category) => {
  return category === 'Fuel Type'
    ? {
        fastCode: yup
          .string()
          .required('Fast code is required.')
          .max(30, 'Max 30 characters allowed.')
          .test(
            'special characters',
            'No special characters except ( ) and -',
            (value) => !codeNoSpecialCharacter(value),
          ),
        shortHand: yup.string().required('ShortHand is required.'),
        allowNewBids: yup.string().required('New Bids is required.'),
      }
    : {};
};

const equipmentCategoryFields = (category) => {
  return category === 'Equipment Category'
    ? {
        sequence: yup
          .string()
          .nullable()
          .required('Sequence is required.')
          .test(
            'sequence',
            'Make sure the Sequence meets the requirements listed below.',
            (value) => getValidator(value, 'Sequence'),
          ),
        selectionType: yup
          .string()
          .required('Selection type is required.')
          .oneOf(['M', 'S'], 'Selection type must be Multiple or Single.'),
      }
    : {};
};

const PocSchema = yup.object().shape({
  firstName: yup.string().required('This is a required field.'),
  lastName: yup.string().required('This is a required field.'),
  email: yup
    .string()
    .email('This is not a valid email.')
    .required('This is a required field.'),
  phoneNumber: yup.string().required('This is a required field.'),
});

const catalogCodesSchema = (
  catalogCodeCategory,
  {
    canAddCoPocs = false,
    requiresParentCode = false,
    validateCode = true,
  } = {},
) => {
  return yup.object().shape({
    // The code is only editable while adding. On edit the form renders it as
    // plain text and the mutation sends the stored code, so validating it there
    // could only ever raise an error with no field to display it on.
    code: validateCode
      ? yup.lazy((value) => {
          const isValid = getValidator(value, catalogCodeCategory);
          if (value !== undefined && !isValid) {
            return yup
              .string()
              .afpValidate(
                'Make sure the code meets the requirements listed below.',
                'code',
                isValid,
              )
              .required();
          }
          return yup.mixed().notRequired();
        })
      : yup.mixed().notRequired(),

    title: yup.lazy((value) => {
      const isValid = getValidator(value, 'Title');
      if (value !== undefined && !isValid) {
        return yup
          .string()
          .afpValidate(
            'Make sure the title meets the requirements listed below.',
            'title',
            isValid,
          )
          .required();
      }
      return yup.mixed().notRequired();
    }),
    // Only categories whose metadata declares a parent category render (and
    // require) a parent code. For the rest the value is absent or null on the
    // record, which must not fail validation on a field the form never shows.
    parentCode: requiresParentCode
      ? yup.string().nullable().required('Parent Category Code is required.')
      : yup.mixed().notRequired(),
    // Description is optional and is stored as null when never filled in.
    description: yup.string().nullable().notRequired(),
    tags: yup.array().of(yup.string()).nullable().notRequired(),
    ...(catalogCodeCategory === 'Vehicle Group'
      ? {
          vehicleGroupPOCs: yup.object().shape({
            PocSeniorEngineer: PocSchema,
            PocPrimaryEngineer: PocSchema,
            ...(canAddCoPocs
              ? {
                  PocSeniorCO: PocSchema,
                  PocPrimaryCO: PocSchema,
                }
              : {}),
          }),
        }
      : {}),
    ...fuelTypeFields(catalogCodeCategory),
    ...equipmentCategoryFields(catalogCodeCategory),
  });
};

const getInputData = (catalogCode) => {
  const pocs = (catalogCode?.standardsVehicleGroupPocs || []).map((poc) => {
    return {
      firstName: poc.name?.split(' ')[0],
      lastName: poc.name?.split(' ')[1],
      pocType: poc.pocType,
      email: poc.email,
      phoneNumber: poc.phoneNumber,
    };
  });
  const vehicleGroupPOCs = keyBy(pocs, 'pocType') || {};
  return {
    category: catalogCode?.category,
    code: catalogCode?.code,
    title: catalogCode?.title,
    tags: catalogCode?.tags?.value || [],
    parentCategory: catalogCode?.parentCategory,
    parentCode: catalogCode?.parentCode,
    additionalProps: catalogCode?.additionalProps || {},
    description: catalogCode?.description,
    vehicleGroupPOCs,
  };
};

const getFormContext = (category, catalogCode, options = {}) => {
  const methods = useForm({
    resolver: yupResolver(catalogCodesSchema(category, options)),
    defaultValues: getInputData(catalogCode),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  return methods;
};

export default getFormContext;
